<template>
  <div class="flex flex-col gap-2 justify-start items-start">
    <div class="flex flex-col">
      <div>
        <img
          loading="lazy"
          :src="expertProfilePicture || 'https://via.placeholder.com/45'"
          alt="expert profile picture"
          class="w-[45px] h-[45px] object-cover rounded-full"
        />
      </div>
    </div>
    <div class="flex flex-col">
      <span class="text-gray-900 font-sans text-2xl font-bold leading-7"
        >Meeting with {{ expertName }}</span
      >
    </div>
    <div class="flex flex-wrap gap-2">
      <div v-html="CLOCK_ICON"></div>
      <span class="text-gray-900/60 font-sans text-base font-bold leading-6"
        >30 min</span
      >
    </div>
    <div class="flex flex-wrap gap-2">
      <div v-html="PHONE_ICON"></div>
      <span class="text-gray-900/60 font-sans text-base font-bold leading-6"
        >Phone call</span
      >
    </div>
    <div class="flex flex-row gap-2" v-if="hasDateAndTime">
      <div v-html="CALENDAR_ICON"></div>
      <span class="text-gray-900/60 font-sans text-base font-bold leading-6">{{
        dateAndTime
      }}</span>
    </div>

    <!-- old booking -->
    <div class="flex flex-col gap-2 mt-4" v-if="previousMeeting">
      <span
        class="text-primary-purple bg-primary-purple/10 px-2 py-1 rounded-md font-sans text-base font-bold leading-6"
        >Previous booking:</span
      >
      <div class="flex flex-row items-center gap-2">
        <div v-html="CALENDAR_ICON"></div>
        <span
          class="text-gray-900/60 font-sans text-base font-bold leading-6"
          >{{ previousMeeting }}</span
        >
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { CLOCK_ICON, PHONE_ICON } from "@/assets/svg/shared/svg-constants";
import { COMPANY_LOGO, CALENDAR_ICON } from "@/assets/svg/shared/svg-constants";
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { gql } from "@apollo/client/core";
import { useQuery } from "@vue/apollo-composable";
import { useSlugQueries } from "@/composables/useSlugQueries";

const GET_EXPERT_BY_UUID = gql`
  query getExpertByUuid($uuid: String!) {
    getExpertByUuid(uuid: $uuid) {
      firstName
      lastName
      profilePicture
    }
  }
`;

export default defineComponent({
  setup() {
    const route = useRoute();
    const previousMeeting = ref("");

    // Use the slug queries composable
    const { isSlugBasedRoute, expertBySlugResult } = useSlugQueries();

    // Fetch expert profile using GraphQL (legacy query parameter routing)
    const expertUuid = computed(() => route.query.expert as string);
    const { result: expertResult } = useQuery(
      GET_EXPERT_BY_UUID,
      () => ({ uuid: expertUuid.value }),
      () => ({ enabled: !!expertUuid.value && !isSlugBasedRoute.value })
    );

    // Consolidated expert data with fallback logic
    const expertData = computed(() => {
      // Use slug-based data if available
      if (expertBySlugResult.value?.getExpertBySlug) {
        return expertBySlugResult.value.getExpertBySlug;
      }
      // Fallback to legacy query parameter routing
      if (expertResult.value?.getExpertByUuid) {
        return expertResult.value.getExpertByUuid;
      }
      return null;
    });

    const expertName = computed(() => {
      if (expertData.value) {
        const { firstName, lastName } = expertData.value;
        return `${firstName} ${lastName}`;
      }
      return "Expert"; // fallback to default text
    });

    const expertProfilePicture = computed(() => {
      return expertData.value?.profilePicture || null;
    });

    const formatDate = (dateString: string): string => {
      const [year, month, day] = dateString.split("-").map(Number);
      const date = new Date(year, month - 1, day);
      // Define options for the date format
      const options: Intl.DateTimeFormatOptions = {
        weekday: "long", // Full weekday name (e.g., "Monday")
        month: "short", // Short month name (e.g., "Nov")
        day: "numeric", // Day of the month (e.g., "18")
        year: "numeric", // Full year (e.g., "2024")
      };

      return new Intl.DateTimeFormat("en-US", options).format(date);
    };

    const formatTimeRange = (timeString: string): string => {
      // Create a Date object for the starting time (8:00 PM)
      const [hour, minute] = timeString.split(":").map(Number);
      const startTime = new Date();
      startTime.setHours(hour, minute, 0, 0);

      // Calculate the ending time (8:30 PM)
      const endTime = new Date(startTime.getTime() + 30 * 60000); // Add 30 minutes

      // Format both times
      const options: Intl.DateTimeFormatOptions = {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      };

      const startFormatted = new Intl.DateTimeFormat("en-US", options).format(
        startTime
      );
      const endFormatted = new Intl.DateTimeFormat("en-US", options).format(
        endTime
      );

      return `${startFormatted} - ${endFormatted}`;
    };

    const hasDateAndTime = computed(() => {
      // Check for the presence of the query param (example: "userId")
      return route.query.date && route.query.startTime;
    });

    const dateAndTime = computed(() => {
      const date = route.query.date as string;
      const time = route.query.startTime as string;
      return `${formatTimeRange(time)}, ${formatDate(date)} `;
    });

    const checkPreviousMeeting = () => {
      if (route.query.oldBooking) {
        const current = route.query.oldBooking as string;
        const dateObject = new Date(current);

        const formattedDateTime = dateObject.toLocaleString("en-US", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });

        previousMeeting.value = formattedDateTime;
      }
    };

    onMounted(() => {
      checkPreviousMeeting();
    });

    return {
      CLOCK_ICON,
      PHONE_ICON,
      COMPANY_LOGO,
      CALENDAR_ICON,
      hasDateAndTime,
      dateAndTime,
      expertName,
      previousMeeting,
      expertProfilePicture,
    };
  },
});
</script>
